import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => body.append(key, val));
      await fetch('https://readdy.ai/api/form/d6s97qcddmmni7ck99n0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  const offices = [
    {
      country: 'USA',
      flag: 'ri-map-pin-2-fill',
      address: '12020 Sunrise Valley Dr, Suite #100,\nReston, VA 21091, USA',
      phone: '+1 (703) 594-6771',
      email: 'info@unitiech.com',
      mapSrc:
        'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3105.0!2d-77.3541!3d38.9586!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b64b0e0e0e0e0e%3A0x0!2s12020+Sunrise+Valley+Dr%2C+Reston%2C+VA+20191!5e0!3m2!1sen!2sus!4v1700000000000',
      highlight: true,
    },
    {
      country: 'India',
      flag: 'ri-map-pin-2-fill',
      address: 'Address coming soon\nHyderabad, India',
      phone: '+91 (000) 000-0000',
      email: 'info@unititech.com',
      mapSrc: '',
      highlight: false,
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-72 flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/3cf9477288b11b475c7f6877e9fb1727.jpg"
          alt="Contact Hero"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E3A5F]/70 via-[#1E3A5F]/60 to-[#1E3A5F]/70"></div>
        <div className="relative z-10 text-center px-6">
          <p className="text-[#14B8A6] text-sm uppercase tracking-widest font-semibold mb-3">Get In Touch</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-white/80 text-lg max-w-xl mx-auto">
            We'd love to hear from you. Reach out to our team and let's start a conversation.
          </p>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#14B8A6] text-sm uppercase tracking-widest font-semibold mb-2">Our Offices</p>
            <h2 className="text-3xl font-bold text-[#1E3A5F]">Global Presence</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {offices.map((office) => (
              <div
                key={office.country}
                className={`rounded-2xl overflow-hidden shadow-md border ${
                  office.highlight ? 'border-[#14B8A6]/40' : 'border-gray-200'
                } bg-white`}
              >
                {/* Map or placeholder */}
                <div className="h-52 w-full bg-gray-100 relative">
                  {office.mapSrc ? (
                    <iframe
                      src={office.mapSrc}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`${office.country} Office Map`}
                    ></iframe>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <div className="w-10 h-10 flex items-center justify-center text-gray-400 mb-2">
                        <i className="ri-map-2-line text-4xl"></i>
                      </div>
                      <p className="text-gray-400 text-sm">Map coming soon</p>
                    </div>
                  )}
                  <div
                    className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold text-white ${
                      office.highlight ? 'bg-[#14B8A6]' : 'bg-[#1E3A5F]'
                    }`}
                  >
                    {office.country}
                  </div>
                </div>

                {/* Info */}
                <div className="p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center text-[#14B8A6] mt-0.5">
                      <i className="ri-map-pin-2-fill text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Address</p>
                      <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{office.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center text-[#14B8A6] mt-0.5">
                      <i className="ri-phone-fill text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Phone</p>
                      <a
                        href={`tel:${office.phone.replace(/\s/g, '')}`}
                        className="text-gray-700 text-sm hover:text-[#14B8A6] transition-colors cursor-pointer"
                      >
                        {office.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center text-[#14B8A6] mt-0.5">
                      <i className="ri-mail-fill text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Email</p>
                      <a
                        href={`mailto:${office.email}`}
                        className="text-gray-700 text-sm hover:text-[#14B8A6] transition-colors cursor-pointer"
                      >
                        {office.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
            {/* Left info */}
            <div className="lg:col-span-2">
              <p className="text-[#14B8A6] text-sm uppercase tracking-widest font-semibold mb-3">Send a Message</p>
              <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6">Let's Start a Conversation</h2>
              <p className="text-gray-600 leading-relaxed mb-10">
                Whether you're looking to kick off a new project, explore our engagement models, or simply learn more about what we do — we're here to help.
              </p>
              <div className="space-y-6">
                {[
                  { icon: 'ri-time-line', title: 'Response Time', desc: 'We typically respond within 1 business day.' },
                  { icon: 'ri-shield-check-line', title: 'Confidential', desc: 'Your information is safe and never shared.' },
                  { icon: 'ri-global-line', title: 'Global Support', desc: 'Teams in USA and India ready to assist you.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#14B8A6]/10 text-[#14B8A6] shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-[#1E3A5F] text-sm">{item.title}</p>
                      <p className="text-gray-500 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#14B8A6]/15 text-[#14B8A6] mb-5">
                    <i className="ri-checkbox-circle-fill text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E3A5F] mb-3">Message Sent!</h3>
                  <p className="text-gray-500 max-w-sm">
                    Thank you for reaching out. Our team will get back to you within 1 business day.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' }); setCharCount(0); }}
                    className="mt-8 px-8 py-3 bg-[#14B8A6] text-white rounded-full font-medium hover:bg-[#0F9B8E] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-gray-50 rounded-2xl p-8 border border-gray-100 shadow-sm space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Full Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Email Address <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (000) 000-0000"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Name"
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Service of Interest
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-[#14B8A6] transition-colors cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option value="Content Management">Content Management</option>
                      <option value="Search and Commerce">Search and Commerce</option>
                      <option value="DevOps and Cloud">DevOps and Cloud</option>
                      <option value="Application Development">Application Development &amp; Digital Transformation</option>
                      <option value="Application Support">Application &amp; Operations Support</option>
                      <option value="Staff Augmentation">Staff Augmentation</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Message <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or inquiry..."
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:border-[#14B8A6] transition-colors resize-none"
                    ></textarea>
                    <p className={`text-xs mt-1 text-right ${charCount >= 500 ? 'text-red-400' : 'text-gray-400'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || charCount > 500}
                    className="w-full py-3.5 bg-[#14B8A6] text-white rounded-full font-semibold hover:bg-[#0F9B8E] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
